import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ChevronLeftIcon, ChevronRightIcon, ShareIcon } from '@heroicons/react/24/outline';
import { DefaultService, PlayOutput, RatingHistory } from '../generated-api';
import { getRatingHistories } from '../services/ratingHistories';
import { useGames } from '../contexts/DataContext';
import { useSession } from '../contexts/SessionContext';
import {
  computeWrapped,
  lastMonth,
  parsePeriod,
  periodHasStarted,
  periodKey,
  periodLabel,
  periodRange,
  shiftPeriod,
  WrapPeriod,
} from '../utils/wrapped';
import { buildWrapCards, WrapCard, WrapPic } from '../utils/wrappedCards';
import { loadWrapImages, shareWrappedImage, WrapImageMap } from '../utils/wrappedImage';
import PlayerAvatar from '../components/common/PlayerAvatar';

const PAGE_SIZE = 200;

/** Every play from `start` on (newest first, paged until older than start). */
const fetchPlaysSince = async (start: Date): Promise<PlayOutput[]> => {
  const all: PlayOutput[] = [];
  for (let offset = 0; ; offset += PAGE_SIZE) {
    const page = await DefaultService.getPlaysPlaysGet({ limit: PAGE_SIZE, offset, sort: 'newest' });
    all.push(...page);
    const oldest = page[page.length - 1];
    if (page.length < PAGE_SIZE || !oldest || new Date(oldest.playDate) < start) return all;
  }
};

const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase();

/** Box art (or a play photo); initials on a tile when there is none or it fails to load. */
const Cover: React.FC<{ pic: WrapPic; className: string; textClass?: string }> = ({ pic, className, textClass = 'text-lg' }) => {
  const [failed, setFailed] = useState(false);
  if (!pic.url || failed) {
    return (
      <div
        className={`${className} bg-primary-100 text-primary-800 font-bold flex items-center justify-center ${textClass}`}
        title={pic.name}
      >
        {initials(pic.name)}
      </div>
    );
  }
  return (
    <img src={pic.url} alt={pic.name} title={pic.name} onError={() => setFailed(true)} className={`${className} object-cover bg-white`} />
  );
};

/** The card's big picture, centred: covers on the group card, else one cover, photo or player. */
const MainPics: React.FC<{ card: WrapCard }> = ({ card }) => {
  if (card.id === 'group') {
    return (
      <div className="flex flex-wrap justify-center gap-2 mb-4">
        {card.pics.map((pic, i) => (
          <Cover key={`${pic.name}-${i}`} pic={pic} className="w-14 h-14 rounded-xl shadow-md ring-2 ring-white" textClass="text-sm" />
        ))}
      </div>
    );
  }
  const pic = card.pics[0];
  if (!pic) return <div className="text-4xl mb-3" aria-hidden="true">{card.emoji}</div>;
  if (pic.kind === 'player') {
    return (
      <div className="flex justify-center mb-4">
        <PlayerAvatar name={pic.name} imageUrl={pic.url} size="xlarge" />
      </div>
    );
  }
  if (pic.kind === 'photo') return <Cover pic={pic} className="block w-full h-44 rounded-2xl shadow-md mb-4" />;
  return <Cover pic={pic} className="block mx-auto w-36 h-36 rounded-2xl shadow-md mb-4" textClass="text-4xl" />;
};

/** Small faces or covers under the text. */
const PicRow: React.FC<{ pics: WrapPic[]; extra?: number }> = ({ pics, extra = 0 }) => (
  <div className="mt-4 flex items-center flex-wrap gap-y-2">
    <div className={`flex items-center ${pics[0]?.kind === 'player' ? '-space-x-3' : 'gap-2'}`}>
      {pics.map((pic, i) =>
        pic.kind === 'player' ? (
          <span key={`${pic.name}-${i}`} title={pic.name}>
            <PlayerAvatar name={pic.name} imageUrl={pic.url} size="small" />
          </span>
        ) : (
          <Cover key={`${pic.name}-${i}`} pic={pic} className="w-11 h-11 rounded-lg shadow ring-2 ring-white" textClass="text-xs" />
        ),
      )}
    </div>
    {extra > 0 && <span className="ml-3 text-sm font-semibold">+{extra}</span>}
  </div>
);

const CardBody: React.FC<{ card: WrapCard; big?: boolean; extraPlayers?: number }> = ({ card, big, extraPlayers }) => (
  <>
    <MainPics card={card} />
    <div className={`text-sm font-semibold uppercase tracking-wide ${big ? 'text-primary-100' : 'text-muted'}`}>
      <span aria-hidden="true">{card.emoji} </span>
      {card.label}
    </div>
    <div className={`mt-2 font-bold break-words ${big ? 'text-3xl text-white' : 'text-2xl text-heading'}`}>{card.value}</div>
    {card.detail && (
      <div
        className={`mt-2 text-lg ${
          big ? 'text-primary-50' : card.tone === 'up' ? 'text-green-700' : 'text-body'
        }`}
      >
        {card.detail}
      </div>
    )}
    {card.row && card.row.length > 0 && (
      <div className={big ? 'text-white' : 'text-body'}>
        <PicRow pics={card.row} extra={extraPlayers} />
      </div>
    )}
  </>
);

const WrappedPage: React.FC = () => {
  const navigate = useNavigate();
  const { period: periodParam } = useParams<{ period?: string }>();
  const period = useMemo(() => parsePeriod(periodParam), [periodParam]);
  const { playerId } = useSession();
  const { games, fetch: fetchGames } = useGames();

  const [plays, setPlays] = useState<PlayOutput[] | null>(null);
  const [histories, setHistories] = useState<RatingHistory[]>([]);
  const [playerNames, setPlayerNames] = useState<Record<string, string>>({});
  const [playerImages, setPlayerImages] = useState<Record<string, string | undefined>>({});
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState(0);
  const [shareState, setShareState] = useState<string | null>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchGames();
  }, [fetchGames]);

  useEffect(() => {
    let cancelled = false;
    setPlays(null);
    setError(null);
    setActive(0);
    trackRef.current?.scrollTo({ left: 0 });
    Promise.all([
      fetchPlaysSince(periodRange(period).start),
      getRatingHistories('global').catch(() => ({})),
      DefaultService.getPlayersPlayersGet().catch(() => []),
    ])
      .then(([p, h, players]) => {
        if (cancelled) return;
        setPlays(p);
        setHistories(Object.values(h));
        setPlayerNames(Object.fromEntries(players.map(pl => [pl.id, pl.name])));
        setPlayerImages(Object.fromEntries(players.map(pl => [pl.id, pl.imageUrl])));
      })
      .catch(() => {
        if (!cancelled) setError("Couldn't load the plays. Try again in a moment.");
      });
    return () => {
      cancelled = true;
    };
  }, [period]);

  const { cards, playerCount } = useMemo(() => {
    if (!plays) return { cards: [], playerCount: 0 };
    const gameNames = Object.fromEntries(games.map(g => [g.id, g.name]));
    const gameImages = Object.fromEntries(games.map(g => [g.id, g.imageUrl]));
    const stats = computeWrapped({ period, plays, histories, playerNames, gameNames, meId: playerId });
    return {
      cards: buildWrapCards(stats, period, { games: gameImages, players: playerImages }),
      playerCount: stats.playerCount,
    };
  }, [plays, histories, playerNames, playerImages, games, period, playerId]);

  // Fetch the pictures for the share image up front, so tapping Share opens the
  // share sheet straight away (a slow await would lose the tap's permission)
  const imagesRef = useRef<Promise<WrapImageMap> | null>(null);
  useEffect(() => {
    imagesRef.current = cards.length > 0 ? loadWrapImages(cards) : null;
  }, [cards]);

  const title = periodLabel(period);
  const prev = shiftPeriod(period, -1);
  const next = shiftPeriod(period, 1);
  const hasNext = periodHasStarted(next);
  // Month <-> year switch: a past year opens on its December, this year on last month
  const otherKind: WrapPeriod = period.kind === 'month'
    ? { kind: 'year', year: period.year }
    : period.year === new Date().getFullYear()
      ? lastMonth()
      : { kind: 'month', year: period.year, month: 12 };

  const onScroll = () => {
    const el = trackRef.current;
    if (!el || el.clientWidth === 0) return;
    setActive(Math.round(el.scrollLeft / el.clientWidth));
  };

  const goTo = (i: number) => {
    const el = trackRef.current;
    if (el) el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' });
    setActive(i);
  };

  const share = async () => {
    setShareState('Preparing…');
    const images = await (imagesRef.current || loadWrapImages(cards));
    const result = await shareWrappedImage(title, cards, `wrapped-${periodKey(period)}.png`, images);
    setShareState(result === 'downloaded' ? 'Image saved' : result === 'failed' ? "Couldn't share" : null);
    if (result !== 'cancelled' && result !== 'shared') setTimeout(() => setShareState(null), 3000);
  };

  return (
    <div className="min-h-screen pb-8">
      <div className="relative pt-14 sm:pt-4 pb-2 px-4">
        <button
          onClick={() => navigate(-1)}
          className="absolute left-4 top-14 sm:top-4 p-3 -ml-3 -mt-1 text-body hover:text-heading"
          aria-label="Back"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <h1 className="text-fluid-h2 font-bold text-heading text-center">{title} wrapped</h1>
      </div>

      <div className="max-w-lg mx-auto px-4">
        <div className="flex items-center justify-between mb-4">
          <Link
            to={`/wrapped/${periodKey(prev)}`}
            data-track="wrapped_prev"
            className="flex items-center gap-1 min-h-[44px] px-2 -ml-2 text-primary-700 font-medium flex-shrink-0"
          >
            <ChevronLeftIcon className="h-5 w-5" /> {periodLabel(prev)}
          </Link>
          <Link
            to={`/wrapped/${periodKey(otherKind)}`}
            data-track="wrapped_switch_kind"
            className="min-h-[44px] flex items-center px-2 text-sm text-body underline flex-shrink-0"
          >
            {otherKind.kind === 'year' ? `Whole ${otherKind.year}` : 'By month'}
          </Link>
          {hasNext ? (
            <Link
              to={`/wrapped/${periodKey(next)}`}
              data-track="wrapped_next"
              className="flex items-center gap-1 min-h-[44px] px-2 -mr-2 text-primary-700 font-medium flex-shrink-0"
            >
              {periodLabel(next)} <ChevronRightIcon className="h-5 w-5" />
            </Link>
          ) : (
            <span className="w-11" />
          )}
        </div>

        {error && <div className="p-4 rounded-2xl bg-red-50 text-red-700">{error}</div>}

        {!error && plays === null && (
          <div className="h-80 rounded-3xl bg-surface border border-edge animate-pulse" data-testid="wrapped-loading" />
        )}

        {!error && plays !== null && cards.length === 0 && (
          <div className="p-8 rounded-3xl bg-surface border border-edge text-center">
            <div className="text-4xl mb-3" aria-hidden="true">🧩</div>
            <p className="text-lg font-semibold text-heading">No games logged in {title}</p>
            <p className="mt-2 text-body">
              <Link to="/plays/new" className="text-primary-700 underline">Log a play</Link> and it shows up here.
            </p>
          </div>
        )}

        {cards.length > 0 && (
          <>
            <div
              ref={trackRef}
              onScroll={onScroll}
              className="flex overflow-x-auto snap-x snap-mandatory -mx-4 [scrollbar-width:none]"
              data-testid="wrapped-track"
            >
              {cards.map((card, i) => (
                <section
                  key={card.id}
                  aria-label={card.label}
                  className="snap-center flex-shrink-0 w-full px-4"
                >
                  <div
                    className={`h-full min-h-[18rem] rounded-3xl p-6 flex flex-col justify-center ${
                      i === 0 ? 'bg-primary-700' : 'bg-surface border-2 border-primary-300'
                    }`}
                  >
                    <CardBody
                      card={card}
                      big={i === 0}
                      extraPlayers={card.id === 'group' ? playerCount - (card.row?.length || 0) : 0}
                    />
                  </div>
                </section>
              ))}
            </div>

            {cards.length > 1 && (
              <div className="flex justify-center gap-1 mt-3" role="tablist" aria-label="Cards">
                {cards.map((card, i) => (
                  <button
                    key={card.id}
                    role="tab"
                    aria-selected={i === active}
                    aria-label={card.label}
                    onClick={() => goTo(i)}
                    className="p-2"
                  >
                    <span className={`block h-2.5 rounded-full transition-all ${i === active ? 'w-6 bg-primary-700' : 'w-2.5 bg-primary-200'}`} />
                  </button>
                ))}
              </div>
            )}

            {cards.length < 3 && (
              <p className="mt-3 text-center text-sm text-body">
                A quiet {period.kind}: more games, more cards.
              </p>
            )}

            <button
              data-track="wrapped_share"
              onClick={share}
              className="mt-5 w-full flex items-center justify-center gap-2 min-h-[52px] rounded-2xl bg-primary-700 hover:bg-primary-800 text-white text-lg font-semibold"
            >
              <ShareIcon className="h-5 w-5" />
              {shareState || 'Share as image'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default WrappedPage;
